import React, { useEffect, useState } from 'react';
import StudentDetailsComponent from './StudentDetailsComponent';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Card, CardContent, Chip, Divider, Typography } from "@mui/material";
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import AttendancePageComponent from "./AttendancePageComponent";
import CommonHeader from "../../base/views/CommonHeader";
import SchedulePageComponent from "./SchedulePageComponent";
import NearestLessonComponent from "../../components/NearestLessonComponent";
import ApiService from "../../base/api/ApiService";
import { Attendance } from "../../components/models/Attendance";
import { Trip } from "../../components/models/Trip";
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

const StudentDetails: React.FC = () => {
    const navigate = useNavigate();
    const { studentId } = useParams<{ studentId: string }>();
    const [visits, setVisits] = useState<Attendance[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [trips, setTrips] = useState<Trip[]>([]);

    useEffect(() => {
        const fetchVisits = async () => {
            try {
                const today = new Date();
                today.setDate(today.getDate() - 4);
                const formattedDate = today.toISOString().slice(0, 10);
                const response = await ApiService.getAttendance(studentId as string, formattedDate);
                setVisits(response);
                setLoading(false);
            } catch (error) {
                console.error('Ошибка получения списка посещений:', error);
                setLoading(false);
            }
        };

        const fetchTrips = async () => {
            try {
                const all = await ApiService.getClientTrips();
                const now = new Date();
                const upcoming = all.filter((t) => {
                    if (t.studentId !== studentId) return false;
                    if (!t.dateStart) return false;
                    return new Date(t.dateStart) >= now || (t.dateEnd ? new Date(t.dateEnd) >= now : false);
                });
                setTrips(upcoming);
            } catch (error) {
                console.error('Ошибка получения поездок:', error);
            }
        };

        fetchVisits();
        fetchTrips();
    }, [studentId]);

    const formatTripDates = (trip: Trip): string => {
        try {
            const start = trip.dateStart ? format(new Date(trip.dateStart), 'd MMM yyyy', { locale: ru }) : '';
            const end = trip.dateEnd ? format(new Date(trip.dateEnd), 'd MMM yyyy', { locale: ru }) : '';
            if (!end || start === end) return start;
            return `${start} — ${end}`;
        } catch {
            return '';
        }
    };

    return (
        <div>
            <CommonHeader title="Подробно" showBackButton={true} />
            <div style={{ margin: '5px' }}>
                <StudentDetailsComponent studentId={studentId as string} />
            </div>
            <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(`/students/${studentId}/payments`)}
                style={{ marginTop: '10px', width: '70%', left: '50%', transform: 'translateX(-50%)' }}
            >
                Оплаты
            </Button>

            {trips.length > 0 && (
                <Box sx={{ mx: '5px', mt: 2 }}>
                    <Box display="flex" alignItems="center" gap={0.5} sx={{ px: 1, mb: 0.5 }}>
                        <DirectionsBusIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight="bold">Предстоящие поездки</Typography>
                    </Box>
                    {trips.map((trip) => (
                        <Card
                            key={trip.tripStudentId ?? trip.id}
                            sx={{ mb: 1, cursor: 'pointer' }}
                            onClick={() => navigate(`/trips/${trip.id}?studentId=${studentId}`)}
                        >
                            <CardContent sx={{ py: '10px !important' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="body2" fontWeight="medium">{trip.name}</Typography>
                                        {trip.nameOfCompetition && (
                                            <Typography variant="caption" color="textSecondary" display="block">
                                                {trip.nameOfCompetition}
                                            </Typography>
                                        )}
                                        {trip.cityOfCompetition && (
                                            <Typography variant="caption" color="textSecondary" display="block">
                                                {trip.cityOfCompetition}
                                            </Typography>
                                        )}
                                        <Typography variant="caption" color="textSecondary" display="block">
                                            {formatTripDates(trip)}
                                        </Typography>
                                        {trip.departureDateAndTime && (
                                            <Typography variant="caption" color="textSecondary" display="block">
                                                Выезд: {(() => { try { return format(new Date(trip.departureDateAndTime!), 'd MMM yyyy, HH:mm', { locale: ru }); } catch { return trip.departureDateAndTime; } })()}
                                            </Typography>
                                        )}
                                        {trip.gatheringPlace && (
                                            <Typography variant="caption" color="textSecondary" display="block">
                                                Сбор: {trip.gatheringPlace}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Chip label="Открыть" size="small" color="primary" variant="outlined" />
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                    <Divider sx={{ mt: 1 }} />
                </Box>
            )}

            <SchedulePageComponent studentId={studentId as string} />
            <AttendancePageComponent studentId={studentId as string} attendance={visits} />
        </div>
    );
};

export default StudentDetails;
